import { useState, useEffect } from "react";
import { useLenis } from "lenis/react";
import { motion } from "framer-motion";
import { LOGO_URL } from "@/data/content";

const LINKS = [
  { label: "Services", id: "services" },
  { label: "Work", id: "work" },
  { label: "Manifesto", id: "manifesto" },
  { label: "Studio", id: "studio" },
];

export default function Navbar() {
  const lenis = useLenis();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (lenis) window.__lenis = lenis;
  }, [lenis]);

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (!el) return;
    if (lenis) lenis.scrollTo(el, { offset: -20 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className="fixed top-0 left-0 right-0 z-50"
      data-testid="navbar"
    >
      <div
        className={`mx-auto flex items-center justify-between px-5 sm:px-8 lg:px-12 transition-all duration-500 ${
          scrolled
            ? "py-3 bg-black/70 backdrop-blur-xl border-b border-white/10"
            : "py-6 bg-transparent border-b border-transparent"
        }`}
      >
        <button
          onClick={() => go("hero")}
          className="flex items-center gap-3"
          data-testid="logo-btn"
        >
          <img src={LOGO_URL} alt="Vision Studio" className="h-9 w-auto object-contain" />
          <span className="hidden sm:block font-display text-sm font-semibold tracking-[0.3em] text-white uppercase">
            Vision Studio
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              data-testid={`nav-${l.id}`}
              className="group relative text-xs font-medium uppercase tracking-[0.2em] text-zinc-400 hover:text-white transition-colors duration-300"
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-white transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => go("contact")}
            data-testid="nav-enquire"
            className="hidden sm:inline-flex items-center rounded-full border border-white/20 bg-white px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.18em] text-black transition-all duration-300 hover:bg-transparent hover:text-white hover:border-white"
          >
            Enquire
          </button>
          <button
            className="md:hidden text-white text-sm uppercase tracking-widest"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-btn"
          >
            {open ? "Close" : "Menu"}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-black/95 backdrop-blur-xl border-b border-white/10 px-5 py-6 flex flex-col gap-5">
          {LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              data-testid={`mobile-nav-${l.id}`}
              className="text-left text-sm uppercase tracking-[0.2em] text-zinc-300"
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => go("contact")}
            className="mt-2 rounded-full bg-white px-6 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-black"
          >
            Enquire
          </button>
        </div>
      )}
    </motion.header>
  );
}
